import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./styles.css";
import { getReviewConversion } from "./conversion.js";
import { createSnapshotPdf, pdfFilename, prefersPdfPreview } from "./pdf-report.js";

const els = {
  form: document.querySelector("#snapshot-form"),
  address: document.querySelector("#address"),
  goal: document.querySelector("#goal"),
  searchButton: document.querySelector("#search-button"),
  message: document.querySelector("#system-message"),
  empty: document.querySelector("#empty-state"),
  loading: document.querySelector("#loading-state"),
  result: document.querySelector("#result"),
  newSearch: document.querySelector("#new-search"),
  print: document.querySelector("#print-button"),
  pdfStatus: document.querySelector("#pdf-status"),
  printCoverAddress: document.querySelector("#print-cover-address"),
  printCoverEyebrow: document.querySelector("#print-cover-eyebrow"),
  printCoverTitle: document.querySelector("#print-cover-title"),
  printCoverSummary: document.querySelector("#print-cover-summary"),
  printCoverMeta: document.querySelector("#print-cover-meta"),
  takeaway: document.querySelector("#takeaway"),
  takeawayEyebrow: document.querySelector("#takeaway-eyebrow"),
  takeawayTitle: document.querySelector("#takeaway-title"),
  takeawaySummary: document.querySelector("#takeaway-summary"),
  priorityWarning: document.querySelector("#priority-warning"),
  priorityWarningTitle: document.querySelector("#priority-warning-title"),
  priorityWarningSummary: document.querySelector("#priority-warning-summary"),
  priorityWarningPath: document.querySelector("#priority-warning-path"),
  mapTitle: document.querySelector("#map-title"),
  mapCaption: document.querySelector("#map-caption"),
  boundaryBadge: document.querySelector("#boundary-badge"),
  analysisAreaControl: document.querySelector("#analysis-area-control"),
  parcelControl: document.querySelector("#parcel-control"),
  propertyAddress: document.querySelector("#property-address"),
  propertyFacts: document.querySelector("#property-facts"),
  findingList: document.querySelector("#finding-list"),
  investigationList: document.querySelector("#investigation-list"),
  leadForm: document.querySelector("#lead-form"),
  leadEmail: document.querySelector("#lead-email"),
  leadConsent: document.querySelector("#lead-consent"),
  leadMessage: document.querySelector("#lead-message"),
  conversionSection: document.querySelector("#conversion-section"),
  reviewEyebrow: document.querySelector("#review-eyebrow"),
  reviewTitle: document.querySelector("#review-title"),
  reviewSummary: document.querySelector("#review-summary"),
  reviewLink: document.querySelector("#review-link"),
  sourceList: document.querySelector("#source-list"),
  limitationList: document.querySelector("#limitation-list"),
  snapshotMeta: document.querySelector("#snapshot-meta")
};

const map = L.map("map", { zoomControl: true, scrollWheelZoom: false }).setView([39.8283, -98.5795], 4);
L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
  maxZoom: 20,
  crossOrigin: true,
  attribution: "© OpenStreetMap contributors"
}).addTo(map);

const mapLayers = {
  analysisArea: L.geoJSON(null, { style: { color: "#9a6816", weight: 3, dashArray: "8 7", fillColor: "#d4a650", fillOpacity: .08 } }).addTo(map),
  parcel: L.geoJSON(null, { style: { color: "#17603f", weight: 4, fillColor: "#5b9a75", fillOpacity: .11 } }).addTo(map),
  flood: L.geoJSON(null, { style: { color: "#126aa1", weight: 2, fillColor: "#2791c9", fillOpacity: .38 } }).addTo(map),
  wetlands: L.geoJSON(null, { style: { color: "#496f36", weight: 2, fillColor: "#739b4d", fillOpacity: .42 } }).addTo(map),
  buildings: L.geoJSON(null, { style: { color: "#6d4f36", weight: 1.5, fillColor: "#9a7a5b", fillOpacity: .28 } }).addTo(map),
  point: L.layerGroup().addTo(map)
};

let config = { parcelReady: false, leadCaptureReady: false, subscriptionRequired: false };
let currentSnapshot = null;
let printOpenStates = [];

initialize();

async function initialize() {
  els.form.addEventListener("submit", submitSnapshot);
  els.newSearch.addEventListener("click", reset);
  els.print.addEventListener("click", saveSnapshotPdf);
  els.leadForm.addEventListener("submit", submitLead);
  document.querySelectorAll("[data-layer]").forEach(control => control.addEventListener("change", toggleLayer));
  window.addEventListener("resize", () => map.invalidateSize());
  window.addEventListener("beforeprint", preparePrint);
  window.addEventListener("afterprint", restoreAfterPrint);
  try {
    config = await api("/api/config");
    els.leadForm.hidden = !config.leadCaptureReady;
  } catch {
    showMessage("The tool configuration could not be loaded. Refresh the page before searching.", "error");
  }
}

async function submitSnapshot(event) {
  event.preventDefault();
  hideMessage();
  if (!els.form.checkValidity()) {
    els.form.reportValidity();
    return;
  }
  setLoading(true);
  try {
    currentSnapshot = await api("/api/snapshot", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ address: els.address.value.trim(), goal: els.goal.value })
    });
    renderSnapshot(currentSnapshot);
  } catch (error) {
    showMessage(error.message, "error");
    els.form.scrollIntoView({ behavior: "smooth", block: "center" });
  } finally {
    setLoading(false);
  }
}

function renderSnapshot(snapshot) {
  els.empty.hidden = true;
  els.result.hidden = false;
  els.takeaway.dataset.level = snapshot.takeaway.level;
  els.takeawayEyebrow.textContent = snapshot.takeaway.eyebrow;
  els.takeawayTitle.textContent = snapshot.takeaway.title;
  els.takeawaySummary.textContent = snapshot.takeaway.summary;
  els.printCoverAddress.textContent = snapshot.property.matchedAddress;
  els.printCoverEyebrow.textContent = snapshot.takeaway.eyebrow;
  els.printCoverTitle.textContent = snapshot.takeaway.title;
  els.printCoverSummary.textContent = snapshot.takeaway.summary;
  els.printCoverMeta.textContent = `${snapshot.snapshotId} · ${formatDate(snapshot.generatedAt)} · Version ${snapshot.version}`;

  const parcelMode = snapshot.analysisMode === "verified_parcel";
  els.mapTitle.textContent = snapshot.property.matchedAddress;
  els.boundaryBadge.textContent = parcelMode ? "Verified parcel polygon" : `${formatNumber(snapshot.screeningRadiusFeet)}-foot address screen`;
  els.boundaryBadge.dataset.mode = snapshot.analysisMode;
  els.propertyAddress.textContent = snapshot.property.matchedAddress;
  const parcelProviderAddress = differentAddress(snapshot.property.parcelProviderAddress, snapshot.property.matchedAddress)
    ? snapshot.property.parcelProviderAddress
    : null;
  setDefinitionList(els.propertyFacts, [
    ["Analysis mode", parcelMode ? "Verified parcel analysis" : "Address-centered preliminary screen"],
    ["Parcel number", snapshot.property.parcelId],
    ["Calculated area", snapshot.property.acres === null ? null : `${snapshot.property.acres} acres`],
    ["County / state", snapshot.property.jurisdiction],
    ["Address-point source", snapshot.property.locationBasis],
    ["Recorded land use", snapshot.property.landUse],
    ["Parcel provider record address", parcelProviderAddress],
    ["Boundary basis", snapshot.property.boundaryStatus]
  ].filter(([, value]) => value !== null && value !== undefined && value !== ""));
  els.mapCaption.textContent = parcelMode
    ? "Returned overlap areas are approximate screening measurements. Compare the provider boundary with a current survey and official local records."
    : `The dashed circle is a ${formatNumber(snapshot.screeningRadiusFeet)}-foot area around the matched address point. It is not a parcel boundary and cannot establish whether returned mapping affects the property.`;

  els.findingList.replaceChildren(...snapshot.findings.map(renderFinding));
  renderPriorityWarning(snapshot);
  els.investigationList.replaceChildren(...snapshot.investigation.map(textItem));
  els.sourceList.replaceChildren(...snapshot.sources.map(renderSource));
  els.limitationList.replaceChildren(...snapshot.limitations.map(textItem));
  els.snapshotMeta.textContent = `${snapshot.snapshotId} · Generated ${formatDate(snapshot.generatedAt)} · Product version ${snapshot.version}`;
  const conversion = getReviewConversion(snapshot);
  els.conversionSection.dataset.mode = conversion.mode;
  els.reviewEyebrow.textContent = conversion.eyebrow;
  els.reviewTitle.textContent = conversion.title;
  els.reviewSummary.textContent = conversion.summary;
  els.reviewLink.textContent = conversion.cta;
  els.reviewLink.href = `https://landsageinsights.com/#contact`;
  renderMap(snapshot);

  els.takeaway.focus({ preventScroll: true });
  requestAnimationFrame(revealResultStart);
}

function renderFinding(item) {
  const article = document.createElement("article");
  article.className = "finding-card";
  article.dataset.status = item.status;
  const row = document.createElement("div");
  row.className = "finding-summary-row";
  const kicker = document.createElement("p");
  kicker.className = "finding-kicker";
  kicker.textContent = `${statusLabels[item.status] || "Review"} · ${item.kicker}`;
  const title = document.createElement("h3");
  title.textContent = item.title;
  const summary = document.createElement("p");
  summary.textContent = item.summary;
  row.append(kicker, title, summary);
  if (item.notice) {
    const notice = document.createElement("p");
    notice.className = "finding-notice";
    notice.textContent = item.notice;
    row.append(notice);
  }

  const details = document.createElement("details");
  const detailsSummary = document.createElement("summary");
  detailsSummary.textContent = "Evidence and next question";
  const list = document.createElement("ul");
  list.append(...(item.evidence || []).map(textItem));
  const question = document.createElement("p");
  question.className = "next-question";
  question.textContent = item.question;
  details.append(detailsSummary, list, question);
  if (item.sourceUrl) {
    const link = document.createElement("a");
    link.href = item.sourceUrl;
    link.target = "_blank";
    link.rel = "noopener";
    link.textContent = "Open zoning source →";
    details.append(link);
  }
  article.append(row, details);
  return article;
}

function renderPriorityWarning(snapshot) {
  const wetlands = snapshot.findings.find(item => item.key === "wetlands");
  const visible = wetlands && ["attention", "watch", "unknown"].includes(wetlands.status);
  els.priorityWarning.hidden = !visible;
  if (!visible) return;
  const unresolved = wetlands.status === "unknown";
  els.priorityWarningTitle.textContent = unresolved
    ? "The wetland check is unresolved—not clear."
    : "Do not treat mapped wetlands as a wetland delineation.";
  els.priorityWarningSummary.textContent = wetlands.notice || wetlands.summary;
  els.priorityWarningPath.textContent = wetlands.question;
}

function renderMap(snapshot) {
  for (const key of ["analysisArea", "parcel", "flood", "wetlands", "buildings"]) mapLayers[key].clearLayers();
  mapLayers.point.clearLayers();
  if (snapshot.map.analysisArea) mapLayers.analysisArea.addData(snapshot.map.analysisArea);
  if (snapshot.map.parcel) mapLayers.parcel.addData(snapshot.map.parcel);
  mapLayers.flood.addData(snapshot.map.flood);
  mapLayers.wetlands.addData(snapshot.map.wetlands);
  mapLayers.buildings.addData(snapshot.map.buildings);
  L.circleMarker([snapshot.property.latitude, snapshot.property.longitude], {
    radius: 5, color: "#fff", weight: 2, fillColor: "#15241d", fillOpacity: 1
  }).bindTooltip(snapshot.property.locationBasis || "Matched address point").addTo(mapLayers.point);
  els.analysisAreaControl.hidden = !snapshot.map.analysisArea;
  els.parcelControl.hidden = !snapshot.map.parcel;
  const activeBoundaryLayer = snapshot.map.parcel ? mapLayers.parcel : mapLayers.analysisArea;
  const fitActiveBoundary = () => {
    map.invalidateSize({ pan: false });
    const bounds = activeBoundaryLayer.getBounds();
    if (bounds.isValid()) map.fitBounds(bounds.pad(.18), { maxZoom: 18 });
  };
  fitActiveBoundary();
  setTimeout(fitActiveBoundary, 80);
}

function revealResultStart() {
  const root = document.documentElement;
  const previousBehavior = root.style.scrollBehavior;
  root.style.scrollBehavior = "auto";
  els.takeaway.scrollIntoView({ block: "start" });
  root.style.scrollBehavior = previousBehavior;
}

async function saveSnapshotPdf() {
  if (!currentSnapshot) return;
  const mobilePreview = prefersPdfPreview({
    userAgent: navigator.userAgent,
    maxTouchPoints: navigator.maxTouchPoints
  });
  const pdfWindow = mobilePreview ? window.open("", "_blank") : null;
  if (pdfWindow) {
    pdfWindow.document.title = "Preparing LandSage PDF";
    pdfWindow.document.body.innerHTML = "<main style=\"font-family:system-ui;padding:32px;color:#173f2d\"><h1 style=\"font-size:24px\">Preparing your LandSage PDF...</h1><p>This tab will display the finished report in a moment.</p></main>";
  }

  els.print.disabled = true;
  els.print.textContent = "Building PDF...";
  els.pdfStatus.hidden = false;
  els.pdfStatus.className = "pdf-status";
  els.pdfStatus.textContent = "Building a formatted report. This may take a few seconds.";

  try {
    const blob = await createSnapshotPdf(currentSnapshot);
    const blobUrl = URL.createObjectURL(blob);
    if (pdfWindow) {
      pdfWindow.location.replace(blobUrl);
    } else {
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = pdfFilename(currentSnapshot);
      document.body.append(link);
      link.click();
      link.remove();
    }
    els.pdfStatus.textContent = pdfWindow
      ? "The PDF opened in a new tab. Use your phone's Share button to save, print, or send it."
      : "Your PDF is ready in your downloads.";
    setTimeout(() => URL.revokeObjectURL(blobUrl), 120000);
  } catch (error) {
    if (pdfWindow) pdfWindow.close();
    els.pdfStatus.className = "pdf-status error";
    els.pdfStatus.textContent = "The PDF could not be created. Please try again or use your browser's Print command.";
    console.error("PDF generation failed", error);
  } finally {
    els.print.disabled = false;
    els.print.textContent = "Download / save PDF";
  }
}

async function submitLead(event) {
  event.preventDefault();
  els.leadMessage.textContent = "";
  if (!els.leadForm.checkValidity()) {
    els.leadForm.reportValidity();
    return;
  }
  const button = els.leadForm.querySelector("button");
  button.disabled = true;
  button.textContent = "Saving…";
  try {
    const result = await api("/api/lead", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ email: els.leadEmail.value, consent: els.leadConsent.checked, snapshot: currentSnapshot })
    });
    els.leadMessage.textContent = result.message;
    els.leadForm.reset();
  } catch (error) {
    els.leadMessage.textContent = error.message;
  } finally {
    button.disabled = false;
    button.textContent = "Save my snapshot";
  }
}

function toggleLayer(event) {
  const layer = mapLayers[event.currentTarget.dataset.layer];
  if (!layer) return;
  if (event.currentTarget.checked) layer.addTo(map);
  else map.removeLayer(layer);
}

function setDefinitionList(container, rows) {
  container.replaceChildren(...rows.flatMap(([label, value]) => {
    const dt = document.createElement("dt");
    const dd = document.createElement("dd");
    dt.textContent = label;
    dd.textContent = value;
    return [dt, dd];
  }));
}

function renderSource(source) {
  const li = document.createElement("li");
  const link = document.createElement("a");
  link.href = source.url;
  link.target = "_blank";
  link.rel = "noopener";
  link.textContent = source.name;
  const status = document.createElement("span");
  status.className = `source-status ${source.status}`;
  status.textContent = sourceStatusLabels[source.status] || String(source.status || "unknown").replaceAll("_", " ");
  li.append(link, status);
  if (source.note) {
    const note = document.createElement("small");
    note.textContent = source.note;
    li.append(note);
  }
  return li;
}

function textItem(text) {
  const li = document.createElement("li");
  li.textContent = text;
  return li;
}

function setLoading(active) {
  els.searchButton.disabled = active;
  els.searchButton.textContent = active ? "Checking public mapping…" : "Scan this property";
  els.loading.hidden = !active;
  if (active) {
    els.result.hidden = true;
    els.empty.hidden = true;
    els.loading.scrollIntoView({ behavior: "smooth", block: "center" });
  }
}

function reset() {
  currentSnapshot = null;
  els.result.hidden = true;
  els.empty.hidden = false;
  els.form.reset();
  hideMessage();
  els.pdfStatus.hidden = true;
  els.pdfStatus.textContent = "";
  els.form.scrollIntoView({ behavior: "smooth", block: "center" });
  els.address.focus({ preventScroll: true });
}

function showMessage(message, type) {
  els.message.hidden = false;
  els.message.className = `system-message ${type}`;
  els.message.textContent = message;
}

function hideMessage() {
  els.message.hidden = true;
  els.message.textContent = "";
}

function formatDate(value) {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

function formatNumber(value) {
  return Number(value).toLocaleString();
}

function differentAddress(providerAddress, matchedAddress) {
  if (!providerAddress) return false;
  const normalize = value => String(value || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  return normalize(providerAddress) !== normalize(matchedAddress);
}

function preparePrint() {
  printOpenStates = [...document.querySelectorAll("details")].map(details => ({ details, open: details.open }));
  for (const item of printOpenStates) item.details.open = true;
  map.invalidateSize();
}

function restoreAfterPrint() {
  for (const item of printOpenStates) item.details.open = item.open;
  printOpenStates = [];
  map.invalidateSize();
}

async function api(url, options = {}) {
  const response = await fetch(url, options);
  let data;
  try { data = await response.json(); } catch { data = {}; }
  if (!response.ok) throw new Error(data?.error?.message || `Request failed (${response.status}).`);
  return data;
}

const statusLabels = {
  attention: "Mapped condition",
  watch: "Worth checking",
  verify: "Verify locally",
  clear: "No mapped overlap found",
  unknown: "Not resolved"
};

const sourceStatusLabels = {
  available: "available",
  unavailable: "unavailable",
  not_configured: "not configured",
  not_found: "no parcel returned"
};
